/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import {shouldLogToEIM} from './utils';

const RESOURCE_TIMING = 'ResourceTiming';
const NAVIGATION_TIMING = 'NavigationTiming';
const PAINT_TIMING = 'PaintTiming';

/**
 * Initialize the window performance object exactly one time.
 *
 * @param {AdobeMetricsRuntime} a - Adobe Metrics object.
 * @param {Window} win - Global Window object.
 */
export default function initPerformance(a: AdobeMetricsRuntime, win: Window & typeof globalThis): void {
  /**
   * Send a PerformanceEntry object to Metrics storage.
   *
   * @param {object} entry A PerformanceEntry object.
   * @param {string} type The type of Metrics record to create.
   * @param {string} event An optional event.
   */
  function sendEntry(entry: PerformanceEntry, type: string, event?: string): void {
    const m = {
      data: entry,
      dateNow: Date.now(),
      event,
      name: 'exc.metrics-browser-runtime.performance',
      recordType: type,
    };
    a.queue.push(m as any);
  }

  /**
   * Send a PerformanceResourceTiming object to Metrics storage.
   * To avoid looping, the resourceTiming object will be ignored
   * if it is an interaction with the telemetry gateway.
   *
   * @param {object} resourceTiming A resourceTiming object.
   * @param {string} event An optional event.
   */
  function sendResourceTiming(resourceTiming: PerformanceResourceTiming, event?: string): void {
    // skip telemetry/metrics POST to avoid looping
    // this bit of defensive coding is because resourceTiming.name.startsWith
    // can be undefined in some browsers, depending on who the
    // underlying Performance object is implemented
    if (
      resourceTiming.name &&
      resourceTiming.name.substring(0, 17) !== 'https://telemetry' &&
      resourceTiming.name.search(/\/ingest/) === -1 &&
      shouldLogToEIM(resourceTiming.name)
    ) {
      sendEntry(resourceTiming as PerformanceEntry, RESOURCE_TIMING, event);
    }
  }

  /**
   * A callback to record ResourceTiming events from
   * a PerformanceObserver instance.
   *
   * @param {object} list The PerformanceObserverEntryList object.
   */
  function recordResource(list: PerformanceObserverEntryList): void {
    const entries = list.getEntries();
    for (let i = 0; i < entries.length; i++) {
      sendResourceTiming(entries[i] as PerformanceResourceTiming);
    }
  }

  /**
   * A callback to record PaintTiming events from
   * a PerformanceObserver instance.
   *
   * @param {object} list The PerformanceObserverEntryList object.
   */
  function recordPaint(list: PerformanceObserverEntryList): void {
    const entries = list.getEntries();
    // using a for{} loop because sometimes entries.call()
    // is null, causing forEach() to fail
    for (let i = 0; i < entries.length; i++) {
      sendEntry(entries[i] as PerformanceEntry, PAINT_TIMING);
    }
  }

  /**
   * A callback to record NavigationTiming events from
   * a PerformanceObserver instance.
   *
   * @param {object} list The PerformanceObserverEntryList object.
   */
  function recordNavigation(list: PerformanceObserverEntryList): void {
    const entries = list.getEntries();
    // using a for{} loop because sometimes entries.call()
    // is null, causing forEach() to fail
    for (let i = 0; i < entries.length; i++) {
      sendEntry(entries[i] as PerformanceEntry, NAVIGATION_TIMING);
    }
  }

  let resourceObserver;
  let paintObserver;
  let navigationObserver;
  const {performance: p, PerformanceObserver} = win;
  const {init} = a as any;
  if (init.performance) {
    return;
  }
  init.performance = {};

  if (p?.getEntriesByType) {
    let entries: PerformanceEntry[] = p.getEntriesByType('resource');
    for (let i = 0; i < entries.length; i++) {
      sendResourceTiming(entries[i] as PerformanceResourceTiming, 'init');
    }
    entries = p.getEntriesByType('paint');
    for (let i = 0; i < entries.length; i++) {
      sendEntry(entries[i] as PerformanceEntry, PAINT_TIMING, 'init');
    }
    entries = p.getEntriesByType('navigation');
    for (let i = 0; i < entries.length; i++) {
      sendEntry(entries[i] as PerformanceEntry, NAVIGATION_TIMING, 'init');
    }
  }

  // Using (global as any) because Typescript doesn't
  // define Window.PerformanceObserver
  if (win.PerformanceObserver) {
    try {
      resourceObserver = new PerformanceObserver(recordResource);
      resourceObserver.observe({entryTypes: ['resource']});
    } catch (e) {
      // not all browsers support all types - ignore
      resourceObserver && resourceObserver.disconnect();
      resourceObserver = undefined;
    }

    try {
      paintObserver = new PerformanceObserver(recordPaint);
      paintObserver.observe({entryTypes: ['paint']});
    } catch (e) {
      // not all browsers support all types - ignore
      paintObserver && paintObserver.disconnect();
      paintObserver = undefined;
    }

    try {
      navigationObserver = new PerformanceObserver(recordNavigation);
      navigationObserver.observe({entryTypes: ['navigation']});
    } catch (e) {
      // not all browsers support all types - ignore
      navigationObserver && navigationObserver.disconnect();
      navigationObserver = undefined;
    }

    init.performance = {
      navigationObserver,
      paintObserver,
      resourceObserver,
    };
  }
}
